import React from "react";
import { motion } from "framer-motion";
import CTA from "./Components/CTA";

const DesignThinking = () => {
    const pillars = [
        {
            title: "Audit & Opportunity Mapping",
            description: "We audit your engineering workflows, business lifecycle, and data flow pipelines to map structural nodes where custom AI agents can automate high-overhead tasks.",
            icon: (
                <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            )
        },
        {
            title: "AI Feasibility & Architecture",
            description: "We design secure private RAG pipelines, choose the optimal LLM parameters, evaluate compute budgets, and define guardrails before you commit to expensive training phases.",
            icon: (
                <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
            )
        },
        {
            title: "Executive ROI & Alignment",
            description: "Led by a Cornell Executive MBA graduate, we align technical model architectures with operational KPIs, compliance metrics, and direct board-level financial ROI.",
            icon: (
                <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            )
        }
    ];

    return (
        <div className="select-none min-h-screen bg-[#fafafa] dark:bg-[#191a1a] transition-colors duration-300">
            {/* Hero Section */}
            <section className="max-w-4xl mx-auto px-6 py-16 lg:py-24 text-center space-y-6">
                <motion.div 
                    initial={{ opacity: 0, y: -10 }} 
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <span className="text-xs font-semibold text-forest uppercase tracking-widest bg-forest/10 px-3 py-1.5 rounded-full">AI Roadmap</span>
                </motion.div>
                <motion.h1 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="text-4xl lg:text-5xl font-extrabold text-zinc-900 dark:text-white tracking-tight leading-tight"
                >
                    AI Strategy & Roadmapping
                </motion.h1>
                <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.25 }}
                    className="text-base lg:text-lg text-zinc-500 dark:text-zinc-400 font-light max-w-2xl mx-auto leading-relaxed"
                >
                    We bridge deep machine learning technology with corporate strategy, mapping out safe paths to deploy autonomous workflows that scale.
                </motion.p>
            </section>

            {/* Content Cards Section */}
            <section className="max-w-5xl mx-auto px-6 pb-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {pillars.map((p, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 + idx * 0.1 }}
                            className="bg-white dark:bg-zinc-900/60 p-8 rounded-3xl border border-zinc-200 dark:border-zinc-800 hover:border-forest/20 shadow-[0_4px_20px_rgba(0,0,0,0.01)] transition-all duration-300"
                        >
                            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6">
                                {p.icon}
                            </div>
                            <h3 className="text-xl font-bold text-zinc-900 dark:text-white mb-3">{p.title}</h3>
                            <p className="text-sm font-light text-zinc-500 dark:text-zinc-400 leading-relaxed">
                                {p.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CTA section */}
            <CTA />
        </div>
    );
};

export default DesignThinking;